import { useCallback, useEffect, useMemo, useState } from "react"
import { Button } from "~/react/ui/button"
import { Alert, AlertContent, AlertIcon } from "~/react/ui/alert"
import { Separator } from "~/react/ui/separator"
import { Icon } from "@/react/ui/icon"
import { Hint } from "~/react/ui/hint"

type EmailAccount = {
  email: string
  name: string
}

type BillingInformation = {
  company_name?: string
  address?: string
  postal_code?: string
  city?: string
  country_code?: string
  country_name?: string
  additional_info?: string
}

type CreditCard = {
  id?: number
  card_type?: string
  last_4?: string
  full_name?: string
  expiration_month?: string
  expiration_year?: string
  payment_method_id?: string
}

type UnavailableDomain = {
  domain: string
  reason?: string
}

type Props = {
  domainCount: number
  emailCount: number
  domains: string[]
  emailAccounts: EmailAccount[]
  paymentMethodId?: string
  billingInformation?: BillingInformation
  creditCard?: CreditCard
  currencyCode?: string
  provider: "google"
  billingCycle?: "monthly" | "yearly"
  emailPriceCents?: number
  redirectUrl?: string
  onGoToDomainSelection?: () => void
  domainOwnerForm?: {
    firstName: string
    lastName: string
    email: string
    company: string
    address: string
    postalCode: string
    city: string
    countryCode: string
  }
  onOrderComplete?: (orderId: number) => void
}

type CheckoutState = "loading" | "ready" | "processing" | "complete" | "error"

interface OrderPayload {
  domain_count: number
  email_count: number
  provider: "google"
  billing_cycle: "monthly" | "yearly"
  domains: string[]
  email_accounts: EmailAccount[]
  redirect_url: string
  domain_prices: Array<{ domain: string; price_cents: number }>
  payment_method_id?: string
  domain_owner?: {
    first_name: string
    last_name: string
    email: string
    company: string
    street_address_1: string
    postal_code: string
    city: string
    country: string
  }
}

export default function CheckoutTab({
  domainCount,
  emailCount,
  domains,
  emailAccounts,
  paymentMethodId,
  creditCard,
  currencyCode,
  provider = "google",
  billingCycle = "monthly",
  emailPriceCents,
  redirectUrl,
  onGoToDomainSelection,
  domainOwnerForm,
  onOrderComplete,
}: Props) {
  const initialHasPaymentMethod = Boolean(paymentMethodId || creditCard)
  const [state, setState] = useState<CheckoutState>(initialHasPaymentMethod ? "ready" : "loading")
  const [error, setError] = useState<string | null>(null)
  const [unavailableDomains, setUnavailableDomains] = useState<UnavailableDomain[]>([])
  const [domainsExpanded, setDomainsExpanded] = useState(false)
  const [emailsExpanded, setEmailsExpanded] = useState(false)
  const [prices, setPrices] = useState<{ domain: number; email: number; domain_prices?: Array<{ domain: string; price_cents: number }> }>({ domain: 0, email: 0 })
  const [domainPrices, setDomainPrices] = useState<Array<{ domain: string; priceCents: number }>>([])
  const [isLoadingPrices, setIsLoadingPrices] = useState(false)

  const effectiveCurrencyCode = (currencyCode || "USD").toUpperCase()

  const currencyFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: effectiveCurrencyCode,
      })
    } catch {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })
    }
  }, [effectiveCurrencyCode])

  const formatCurrency = useCallback(
    (amountInCurrency: number) => currencyFormatter.format(amountInCurrency),
    [currencyFormatter],
  )

  useEffect(() => {
    const fetchPrices = async () => {
      setIsLoadingPrices(true)
      try {
        const response = await fetch("/email_accounts/checkout", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "",
          },
          body: JSON.stringify({
            domain_count: domainCount,
            email_count: emailCount,
            provider: provider,
            billing_cycle: billingCycle,
            domains: domains,
          }),
        })

        const result = await response.json()
        if (result.prices) {
          setPrices({
            domain: result.prices.domain,
            email: result.prices.email,
            domain_prices: result.prices.domain_prices,
          })
          // Store domain prices for submission
          if (result.prices.domain_prices) {
            setDomainPrices(
              result.prices.domain_prices.map((dp: { domain: string; price_cents: number }) => ({
                domain: dp.domain,
                priceCents: dp.price_cents,
              })),
            )
          } else {
            setDomainPrices([])
          }
        }
      } catch (err) {
        console.error("Failed to fetch prices:", err)
        setDomainPrices([])
      } finally {
        setIsLoadingPrices(false)
      }
    }

    if (domainCount > 0 || emailCount > 0) {
      fetchPrices()
    } else {
      // Clear prices when no domains/emails
      setDomainPrices([])
      setPrices({ domain: 0, email: 0 })
      setIsLoadingPrices(false)
    }
  }, [domainCount, emailCount, provider, billingCycle, domains])

  // Use backend prices as source of truth (already in user's currency)
  const totalDomainPrice = useMemo(() => {
    const priceCents = prices.domain || 0
    const price = priceCents / 100
    return isNaN(price) || !isFinite(price) ? 0 : price
  }, [prices.domain])

  const emailPrice = useMemo(() => {
    const cents = prices.email || emailPriceCents || 0
    const result = cents / 100
    return isNaN(result) || !isFinite(result) ? 0 : result
  }, [emailPriceCents, prices.email])

  // Helper to get individual domain price in user's currency
  const getDomainPrice = useCallback(
    (domain: string): number => {
      // Normalize domain for comparison (lowercase and trim)
      const normalizedDomain = domain.toLowerCase().trim()
      // Look up the actual price for this domain from domainPrices
      const domainPrice = domainPrices.find((dp) => dp.domain.toLowerCase().trim() === normalizedDomain)
      if (domainPrice && domainPrice.priceCents > 0) {
        return domainPrice.priceCents / 100
      }

      return 0
    },
    [domainPrices],
  )

  const domainTotal = typeof totalDomainPrice === "number"
    ? totalDomainPrice
    : 0
  const emailTotal = emailCount * emailPrice
  const grandTotal = domainTotal + emailTotal
  const emailRenewalDate = new Date()
  emailRenewalDate.setMonth(emailRenewalDate.getMonth() + 1)

  const domainRenewalDate = new Date()
  domainRenewalDate.setFullYear(domainRenewalDate.getFullYear() + 1)

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      }

  const hasPaymentMethod = Boolean(paymentMethodId || creditCard)

  const getDisabledReason = (): string | undefined => {
    if (isLoadingPrices) {
      return i18n.t("js.email_accounts.checkout.loading_prices")
    }
    if (!hasPaymentMethod) {
      return i18n.t("js.email_accounts.checkout.payment_method_required")
    }
    if (state === "processing") {
      return i18n.t("js.email_accounts.checkout.processing_payment")
    }
    if (state === "loading") {
      return i18n.t("js.email_accounts.checkout.loading")
      }
    return undefined
  }

  useEffect(() => {
    if (hasPaymentMethod) {
      setState("ready")
    } else {
      setState("loading")
    }
  }, [hasPaymentMethod])

  const checkDomainsAvailability = useCallback(async (): Promise<UnavailableDomain[]> => {
    if (domains.length === 0) return []

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""
    const unavailable: UnavailableDomain[] = []

    const checks = domains.map(async (domain) => {
      try {
        const response = await fetch("/mailpool/check_domain", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ domain }),
        })

        if (!response.ok) {
          console.error(`Domain check failed for ${domain}: HTTP ${response.status}`)
          return
        }

        const result = await response.json()

        if (result.available === false) {
          unavailable.push({ domain })
        }
      } catch (err) {
        console.error(`Failed to check domain ${domain}:`, err)
        // Don't block checkout on check failure
      }
    })

    await Promise.all(checks)
    return unavailable
  }, [domains])

  const handleSubmit = useCallback(async () => {
    const methodId = paymentMethodId
    if (!hasPaymentMethod) {
      setError(i18n.t("js.email_accounts.checkout.payment_method_required"))
      return
    }

    setState("processing")
    setError(null)
    setUnavailableDomains([])

    try {
      if (domains.length > 0) {
        const unavailable = await checkDomainsAvailability()
        if (unavailable.length > 0) {
          setUnavailableDomains(unavailable)
          setState("ready")
          return
        }
      }

      const payload: OrderPayload = {
        domain_count: domainCount,
        email_count: emailCount,
        provider: provider,
        billing_cycle: billingCycle,
        domains: domains,
        email_accounts: emailAccounts,
        redirect_url: redirectUrl || "",
        domain_prices: (domainPrices || [])
          .filter((dp) => typeof dp.priceCents === "number" && dp.priceCents > 0)
          .map((dp) => ({
            domain: dp.domain,
            price_cents: Math.round(dp.priceCents),
          })),
      }
      if (methodId) {
        payload.payment_method_id = methodId
      }
      if (domainOwnerForm) {
        payload.domain_owner = {
          first_name: domainOwnerForm.firstName,
          last_name: domainOwnerForm.lastName,
          email: domainOwnerForm.email,
          company: domainOwnerForm.company,
          street_address_1: domainOwnerForm.address,
          postal_code: domainOwnerForm.postalCode,
          city: domainOwnerForm.city,
          country: domainOwnerForm.countryCode,
        }
      }

      const response = await fetch("/email_accounts/orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "",
        },
        body: JSON.stringify(payload),
      })

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.error || i18n.t("js.email_accounts.checkout.subscription_failed"))
      }

      if (onOrderComplete && result.order_id) {
        onOrderComplete(result.order_id)
      } else {
        setState("complete")
      }
    } catch (err) {
      console.error("Payment error:", err)
      setError(err instanceof Error ? err.message : i18n.t("js.email_accounts.checkout.payment_failed"))
      setState("ready")
    }
  }, [domainCount, emailCount, hasPaymentMethod, paymentMethodId, provider, billingCycle, domains, emailAccounts, checkDomainsAvailability, onOrderComplete])

  if (state === "complete") {
    return (
      <div className="d-flex justify-content-center">
        <div className="checkout-tab__container p-10 text-center">
          <div className="mb-4">
            <span className="far fa-check-circle" style={{ fontSize: 48, color: "#22c55e" }} />
          </div>
          <h3 className="text-lg-medium text-dark mb-2">
            {i18n.t("js.email_accounts.checkout.complete_title")}
          </h3>
        <Hint className="mb-0">
          {i18n.t("js.email_accounts.checkout.complete_description")}
        </Hint>
        </div>
      </div>
    )
  }

  return (
    <div className="email-accounts-provisioning">
      {/* Order Summary Header */}
      <div className="mb-4">
        <h2 className="text-lg-medium mb-1">
          {i18n.t("js.email_accounts.checkout.review_title")}
        </h2>
        <Hint className="mb-6">
          {i18n.t("js.email_accounts.checkout.review_subtitle")}
        </Hint>
      </div>

      {/* Unavailable Domain Alert */}
      {unavailableDomains.length > 0 && (
        <Alert variant="destructive" className="mb-4">
          <AlertContent>
            <div className="d-flex align-items-start gap-3">
              <span className="far fa-exclamation-circle text-danger mt-1" style={{ fontSize: 16 }} />
              <div className="flex-1">
                <div className="text-sm-medium text-danger mb-1">
                  {unavailableDomains.length === 1
                    ? i18n.t("js.email_accounts.checkout.domain_unavailable_title")
                    : i18n.t("js.email_accounts.checkout.domains_unavailable_title", { count: unavailableDomains.length })}
                </div>
                <Hint className="text-dark mb-3">
                  {unavailableDomains.length === 1
                    ? i18n.t("js.email_accounts.checkout.domain_unavailable_description", { domain: unavailableDomains[0].domain })
                    : i18n.t("js.email_accounts.checkout.domains_unavailable_description", { domains: unavailableDomains.map(d => d.domain).join(", ") })}
                </Hint>
                {onGoToDomainSelection && (
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={() => {
                      setUnavailableDomains([])
                      onGoToDomainSelection()
                    }}
                  >
                    {i18n.t("js.email_accounts.checkout.go_to_domain_selection")}
                  </Button>
                )}
              </div>
            </div>
          </AlertContent>
        </Alert>
      )}

      {/* Order Summary Card */}
      <section className="checkout-overview">
        <div className="checkout-overview__content">
          {/* Domains Section */}
          {domainCount > 0 && (
          <>
            <div className="d-flex align-items-center gap-1 mb-2">
              <Icon icon="globe" size="sm" className="text-hint" />
              <h3 className="text-xs-medium m-0">{i18n.t("js.email_accounts.checkout.domains")}</h3>
              <Hint className="ml-auto m-0">
                {i18n.t("js.email_accounts.checkout.billed_yearly")}
              </Hint>
            </div>

            <div className="d-flex align-items-center text-xs">
              {i18n.t("js.email_accounts.provisioning.order_summary_domains_label", { count: domainCount })}
              &nbsp;·&nbsp;
              <Button
                onClick={() => setDomainsExpanded(!domainsExpanded)}
                variant="linkGrey"
                size="xs"
                className="mr-05"
              >
                {i18n.t("js.email_accounts.checkout.details")}
              </Button>
              <Icon icon={domainsExpanded ? "angle-up" : "angle-down"} size="xs" />

              <strong className="fw-medium ml-auto">{formatCurrency(domainTotal)}</strong>
            </div>

            {/* Expanded domains list */}
            {domainsExpanded && (
              <div className="mt-3 text-2xs">
                {domains.map((domain, index) => (
                  <div key={domain} className={`d-flex gap-4${index !== 0 ? " mt-2" : ""}`}>
                    <span className="flex-grow-1">{domain}</span>
                    {formatCurrency(getDomainPrice(domain))}
                  </div>
                ))}
              </div>
            )}
          </>
          )}

          {/* Divider */}
          {domainCount > 0 && emailCount > 0 && (
            <Separator className="my-4" />
          )}
          {/* Email Accounts Section */}
          {emailCount > 0 && (
          <>
            <div className="d-flex align-items-center gap-1 mb-2">
              <Icon icon="globe" size="sm" className="text-hint" />
              <h3 className="text-xs-medium m-0">
                {i18n.t("js.email_accounts.checkout.email_accounts")}
              </h3>
              <Hint className="ml-auto m-0">
                {i18n.t("js.email_accounts.checkout.billed_monthly")}
              </Hint>
            </div>

            <div className="d-flex align-items-center text-xs">
              {i18n.t("js.email_accounts.provisioning.order_summary_accounts_label", { count: emailCount })}
              &nbsp;·&nbsp;
              <Button
                onClick={() => setEmailsExpanded(!emailsExpanded)}
                variant="linkGrey"
                size="xs"
                className="mr-05"
              >
                {i18n.t("js.email_accounts.checkout.details")}
              </Button>
              <Icon icon={emailsExpanded ? "angle-up" : "angle-down"} size="xs" />

              <strong className="fw-medium ml-auto">{formatCurrency(emailTotal)}</strong>
            </div>

            {/* Expanded domains list */}
            {emailsExpanded && (
              <div className="mt-3 text-2xs">
                {emailAccounts.map((account, index) => (
                  <div key={account.email} className={`d-flex gap-4${index !== 0 ? " mt-2" : ""}`}>
                    <span className="flex-grow-1">{account.email}</span>
                    {formatCurrency(emailPrice)}
                  </div>
                ))}
              </div>
            )}
          </>
          )}
        </div>

        <footer className="checkout-overview__footer">
          <div className="d-flex justify-content-between align-items-center text-sm-medium mb-1">
            {i18n.t("js.email_accounts.checkout.total_due_today")}
            <span>{formatCurrency(grandTotal)}</span>
          </div>
          {emailCount > 0 && (
            <Hint>
              {i18n.t("js.email_accounts.checkout.email_accounts_renewal", { date: formatDate(emailRenewalDate) })}
            </Hint>
          )}
          {domainCount > 0 && (
            <Hint className="mt-1">
              {i18n.t("js.email_accounts.checkout.domains_renewal", { date: formatDate(domainRenewalDate) })}
            </Hint>
          )}
        </footer>
      </section>

      {/* Error Message */}
      {error && (
        <Alert variant="warning" className="mt-4 mb-4">
          <AlertIcon>
            <Icon className="text-warning" icon="exclamation-triangle" aria-hidden="true" />
          </AlertIcon>
          <AlertContent>
            {error}
          </AlertContent>
        </Alert>
      )}

      {/* Submit Button */}
      <div
        className="position-relative mb-2 mt-4"
        title={(!hasPaymentMethod || state === "processing" || state === "loading" || isLoadingPrices) ? getDisabledReason() : undefined}
      >
        <Button
          onClick={handleSubmit}
          disabled={!hasPaymentMethod || state === "processing" || state === "loading" || isLoadingPrices}
          variant="primary"
          fullWidth
        >
          {state === "processing" ? (
            <>
              <Icon icon="spinner-third" size="sm" className="fa-spin" />
              {i18n.t("js.email_accounts.checkout.processing")}
            </>
          ) : (
            <>
              {i18n.t("js.email_accounts.checkout.pay_now")} · {formatCurrency(grandTotal)}
            </>
          )}
        </Button>
      </div>

      {/* Terms */}
      <Hint className="text-center">
        {i18n.t("js.email_accounts.checkout.terms_prefix")}{" "}
        <a href="/terms-of-service" target="_blank">
          {i18n.t("js.email_accounts.checkout.terms_link")}
        </a>
        .
      </Hint>
    </div>
  )
}
